import { useState } from 'react';
import { Indicator } from '@mantine/core';
import { IconSearch, IconMessage, IconPhone, IconMail } from '@tabler/icons-react';
import { Avatar, Badge, Box, Button, Card, Group, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@/components/ui';
import { mockChatContacts, ChatContact } from '../mockData/chat';

const getStatusColor = (status: ChatContact['status']) => {
  switch (status) {
    case 'online': return 'green';
    case 'away': return 'yellow';
    case 'busy': return 'red';
    case 'offline': return 'gray';
    default: return 'gray';
  }
};

const formatLastSeen = (date?: Date) => {
  if (!date) return '';
  const now = new Date();
  const diffMins = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

export default function ContactsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  const statuses = ['online', 'away', 'busy', 'offline'];
  const departments = [...new Set(mockChatContacts.map(c => c.department))];

  const filteredContacts = mockChatContacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(search.toLowerCase()) ||
                          contact.email.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || contact.status === statusFilter;
    const matchesDepartment = !departmentFilter || contact.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // Sort by online status first, then by name
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    const statusOrder = ['online', 'away', 'busy', 'offline'];
    const statusDiff = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    if (statusDiff !== 0) return statusDiff;
    return a.name.localeCompare(b.name);
  });

  const onlineCount = mockChatContacts.filter(c => c.status === 'online').length;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Contacts</Title>
          <Text c="dimmed" size="sm">{filteredContacts.length} contacts ({onlineCount} online)</Text>
        </Box>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search contacts..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Department"
            data={departments.map(d => ({ value: d, label: d }))}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Contacts Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {sortedContacts.map(contact => (
          <Card key={contact.id}>
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="sm">
                  <Indicator
                    color={getStatusColor(contact.status)}
                    position="bottom-end"
                    size={12}
                    offset={4}
                  >
                    <Avatar size="lg" radius="xl" color="blue">
                      {contact.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                  </Indicator>
                  <Box>
                    <Text fw={600}>{contact.name}</Text>
                    <Text size="sm" c="dimmed">{contact.department}</Text>
                  </Box>
                </Group>
                <Badge size="xs" color={getStatusColor(contact.status)} variant="dot">
                  {contact.status}
                </Badge>
              </Group>

              <Stack gap={4}>
                <Group gap="xs">
                  <IconMail size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm">{contact.email}</Text>
                </Group>
                {contact.status === 'offline' && contact.lastSeen && (
                  <Text size="xs" c="dimmed">
                    Last seen: {formatLastSeen(contact.lastSeen)}
                  </Text>
                )}
              </Stack>

              <Group>
                <Button variant="light" size="xs" leftSection={<IconMessage size={14} />} style={{ flex: 1 }}>
                  Message
                </Button>
                <Button variant="light" size="xs" leftSection={<IconPhone size={14} />} style={{ flex: 1 }}>
                  Call
                </Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
